import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Plus } from 'lucide-react';
import { BOOKING_STATUSES, type BookingDto, type BookingStatus } from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { useAuth } from '@/features/auth/useAuth';
import { BookingDrawer } from './BookingDrawer';
import { CancelBookingDialog } from './CancelBookingDialog';
import { SeatBookingDialog } from './SeatBookingDialog';
import { groupByHour, partyName, timeOfDay } from './bookingLogic';
import {
  DEFAULT_BOOKING_FILTERS,
  markNoShow,
  useBookings,
  type BookingFilters,
} from './useBookings';

/**
 * The bookings board (WAVE2-DESIGN §1): one trading day's advance bookings,
 * grouped by starting hour, with the whole lifecycle on each row — seat, edit,
 * cancel, no-show. NO_SHOW is always a person's click; nothing here resolves a
 * booking on a timer, the overdue highlight only nags.
 */
export function BookingsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canManage = can('sessions.manage');

  const [filters, setFilters] = useState<BookingFilters>(DEFAULT_BOOKING_FILTERS);
  const { bookings, loading, error, reload } = useBookings(filters);

  /** null = closed; 'create' = new; otherwise the booking being edited. */
  const [drawer, setDrawer] = useState<'create' | BookingDto | null>(null);
  const [cancelling, setCancelling] = useState<BookingDto | null>(null);
  const [seating, setSeating] = useState<BookingDto | null>(null);
  /**
   * The page banner: warn-only create outcomes surfaced after the drawer
   * closes, and any no-show failure that deserves a human's attention.
   */
  const [notice, setNotice] = useState<string | null>(null);

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('bookings.title')}</h1>

        {/* Phone: the date and the status chips fold behind one Filters
            button — five chips on one unwrapping line were wider than the
            screen, and pushed the whole page sideways. Desktop: the same row. */}
        <FilterDisclosure
          storageKey="bookings.filters"
          badge={(filters.businessDate ? 1 : 0) + (filters.status ? 1 : 0)}
        >
          {/* Empty means "the server's default" — today's TRADING day, which is
              not always today's calendar date. */}
          <input
            type="date"
            value={filters.businessDate ?? ''}
            onChange={(event) =>
              setFilters((current) => ({ ...current, businessDate: event.target.value || null }))
            }
            aria-label={t('bookings.date')}
            className={`${controlClass} tabular`}
          />

          <div className="flex flex-wrap items-center gap-1">
            <StatusChip
              label={t('bookings.allStatuses')}
              active={filters.status === null}
              onClick={() => setFilters((current) => ({ ...current, status: null }))}
            />
            {BOOKING_STATUSES.map((status) => (
              <StatusChip
                key={status}
                label={t(`bookings.status.${status}`)}
                active={filters.status === status}
                onClick={() =>
                  setFilters((current) => ({
                    ...current,
                    status: current.status === status ? null : (status as BookingStatus),
                  }))
                }
              />
            ))}
          </div>
        </FilterDisclosure>

        <div className="flex-1" />

        {canManage ? (
          <button
            type="button"
            onClick={() => setDrawer('create')}
            className="rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover"
          >
            <span className="inline-flex items-center gap-1">
              <Plus className="h-4 w-4" aria-hidden />
              {t('bookings.newBooking')}
            </span>
          </button>
        ) : null}
      </header>

      {notice ? (
        <div
          role="status"
          className="mx-4 mb-2 flex items-center gap-2 rounded-control bg-ending/15 px-3 py-2 text-sm text-content"
        >
          <CalendarClock className="h-4 w-4 shrink-0 text-ending" aria-hidden />
          <span className="flex-1">{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="text-xs text-content-secondary hover:text-content"
          >
            {t('bookings.dismiss')}
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !bookings ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : !bookings || bookings.length === 0 ? (
          <p className="text-sm text-content-muted">{t('bookings.noBookings')}</p>
        ) : (
          <BookingsBoard
            bookings={bookings}
            canManage={canManage}
            onSeat={setSeating}
            onEdit={(booking) => setDrawer(booking)}
            onCancel={setCancelling}
            onNoShow={(booking) => void handleNoShow(booking)}
          />
        )}
      </div>

      {drawer ? (
        <BookingDrawer
          booking={drawer === 'create' ? null : drawer}
          onClose={() => setDrawer(null)}
          onSaved={(warnings) => {
            if (warnings.includes('live_session_overlap')) {
              setNotice(t('bookings.liveSessionWarning'));
            }
            void reload();
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelBookingDialog
          booking={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={() => void reload()}
        />
      ) : null}

      {seating ? (
        <SeatBookingDialog
          booking={seating}
          onClose={() => setSeating(null)}
          onSeated={() => void reload()}
        />
      ) : null}
    </div>
  );

  async function handleNoShow(booking: BookingDto): Promise<void> {
    // Only the genuine lost race (`booking_not_open` — a colleague seated or
    // cancelled first) stays silent; any other failure lands in the notice
    // banner, message and all. The reload runs either way. See markNoShow.
    await markNoShow(booking.id, {
      fallbackMessage: t('bookings.errors.saveFailed'),
      onFailure: setNotice,
      reload: () => void reload(),
    });
  }
}

/**
 * The day's rows, grouped by starting hour. Pure — everything it shows comes
 * in through props — so the render suite can pin the overdue highlight and the
 * per-status action set without a DOM.
 */
export function BookingsBoard({
  bookings,
  canManage,
  onSeat,
  onEdit,
  onCancel,
  onNoShow,
}: {
  bookings: BookingDto[];
  canManage: boolean;
  onSeat: (booking: BookingDto) => void;
  onEdit: (booking: BookingDto) => void;
  onCancel: (booking: BookingDto) => void;
  onNoShow: (booking: BookingDto) => void;
}) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByHour(bookings), [bookings]);

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <section key={group.hour}>
          <h2 className="tabular mb-1 text-xs font-semibold uppercase tracking-wide text-content-muted">
            {group.hour}
          </h2>
          <div className="overflow-hidden rounded-card border border-subtle">
            {group.bookings.map((booking, index) => (
              <article
                key={booking.id}
                data-booking-id={booking.id}
                data-overdue={booking.isOverdue}
                className={[
                  'flex flex-wrap items-center gap-3 px-3 py-2',
                  index % 2 ? 'bg-row-even' : 'bg-row-odd',
                  booking.isOverdue ? 'border-s-2 border-danger' : '',
                ].join(' ')}
              >
                <span className="tabular w-24 text-sm text-content-secondary">
                  {timeOfDay(booking.startsAt)}–{timeOfDay(booking.endsAt)}
                </span>
                <span className="w-28 truncate text-sm font-medium text-content">
                  {booking.deviceName}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-content">
                  {partyName(booking)}
                  {booking.partySize ? (
                    <span className="text-content-muted"> · {booking.partySize}</span>
                  ) : null}
                  {booking.note ? (
                    <span className="text-content-muted"> — {booking.note}</span>
                  ) : null}
                </span>

                {booking.isOverdue ? (
                  <span className="rounded-full bg-danger/15 px-2 py-0.5 text-xs font-medium text-danger">
                    {t('bookings.overdue')}
                  </span>
                ) : null}
                <span
                  data-status={booking.status}
                  className={`rounded-full px-2 py-0.5 text-xs ${statusChipClass(booking.status)}`}
                >
                  {t(`bookings.status.${booking.status}`)}
                </span>

                {canManage && booking.status === 'BOOKED' ? (
                  <span className="flex items-center gap-1">
                    <RowButton
                      primary
                      label={t('bookings.actions.seat')}
                      onClick={() => onSeat(booking)}
                    />
                    <RowButton label={t('bookings.actions.edit')} onClick={() => onEdit(booking)} />
                    <RowButton
                      label={t('bookings.actions.noShow')}
                      onClick={() => onNoShow(booking)}
                    />
                    <RowButton
                      label={t('bookings.actions.cancel')}
                      onClick={() => onCancel(booking)}
                    />
                  </span>
                ) : booking.status === 'CANCELLED' && booking.cancelReason ? (
                  <span
                    className="max-w-48 truncate text-xs text-content-muted"
                    title={booking.cancelReason}
                  >
                    {booking.cancelReason}
                  </span>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function statusChipClass(status: BookingDto['status']): string {
  switch (status) {
    case 'BOOKED':
      return 'bg-primary/15 text-primary';
    case 'SEATED':
      return 'bg-available/15 text-available';
    case 'NO_SHOW':
      return 'bg-danger/15 text-danger';
    default:
      return 'bg-hover text-content-muted';
  }
}

function StatusChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        // Finger-height on a phone, where these live in the Filters fold.
        'rounded-full px-3 py-1 text-xs transition-colors compact:min-h-10',
        active
          ? 'bg-primary font-medium text-primary-fg'
          : 'text-content-secondary hover:bg-hover hover:text-content',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function RowButton({
  label,
  onClick,
  primary,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full px-2.5 py-1 text-xs transition-colors',
        primary
          ? 'bg-primary font-medium text-primary-fg hover:bg-primary-hover'
          : 'text-content-secondary hover:bg-hover hover:text-content',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
